import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, X, ArrowRight, Clock } from "lucide-react";
import { DEFCON_COLORS } from "@/lib/defcon";

const DKEY = "ff_defcon_dismissed";

function fmtCountdown(ms) {
  if (ms == null || isNaN(ms)) return "";
  if (ms <= 0) return "starting now";
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (days > 0) return `in ${days}d ${h}h`;
  if (h > 0) return `in ${h}h ${m}m`;
  return `in ${m}m ${s % 60}s`;
}

// Site-wide flashing alert shown to everyone when Crashout hits DEFCON 2 or 1.
// Sticky under the navbar so it stays visible while scrolling. Dismissible, but
// re-appears whenever the level changes again (keyed on level + change time).
export default function DefconAlertBanner() {
  const [d, setD] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [dismissedKey, setDismissedKey] = useState(() => {
    try { return sessionStorage.getItem(DKEY) || ""; } catch { return ""; }
  });

  const load = async () => {
    try {
      const { data } = await api.get("/defcon");
      setD(data);
    } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  // 1s tick so the DEFCON 1 countdown stays live.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!d || !d.show_id) return null;
  const level = d.current_level || 5;
  if (level > 2) return null; // only DEFCON 2 & 1 raise the alarm

  const key = `${level}|${d.level_changed_at || ""}`;
  if (dismissedKey === key) return null;

  const color = DEFCON_COLORS[level] || "#FF3B30";
  const meta = (d.levels && d.levels[String(level)]) || {};
  const nuclear = level === 1;
  const countdown = nuclear && d.next_air ? fmtCountdown(new Date(d.next_air).getTime() - now) : "";

  const dismiss = () => {
    try { sessionStorage.setItem(DKEY, key); } catch {}
    setDismissedKey(key);
  };

  return (
    <div
      className="defcon-alert sticky top-16 z-40 defcon-flash"
      style={{ background: color, color: "#fff", boxShadow: `0 0 30px ${color}99` }}
      data-testid="defcon-alert-banner"
      data-level={level}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-2.5 flex items-center gap-3">
        <AlertTriangle size={16} className={nuclear ? "shrink-0" : "shrink-0"} />
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap font-mono-ui text-[0.7rem] sm:text-xs uppercase tracking-[0.15em]">
          <span className="font-bold">{nuclear ? "☢ DEFCON 1" : "DEFCON 2"} · {d.show_title}</span>
          <span className="opacity-90 normal-case tracking-normal font-sans">{meta.label}{meta.description ? ` — ${meta.description}` : ""}</span>
          {nuclear && d.next_air_label && (
            <span className="inline-flex items-center gap-1.5 font-bold bg-black/25 px-2 py-0.5" data-testid="defcon-next-air">
              <Clock size={12} /> Next: {d.next_air_label}{countdown ? ` · ${countdown}` : ""}
            </span>
          )}
        </div>
        {d.stream_url ? (
          <a
            href={d.stream_url}
            target="_blank"
            rel="noreferrer"
            className="hidden sm:inline-flex items-center gap-1 font-mono-ui text-[0.7rem] uppercase tracking-[0.18em] font-bold underline decoration-2 underline-offset-2 hover:opacity-80 shrink-0"
            data-testid="defcon-alert-cta"
          >
            Watch live <ArrowRight size={13} />
          </a>
        ) : (
          <Link
            to="/defcon"
            className="hidden sm:inline-flex items-center gap-1 font-mono-ui text-[0.7rem] uppercase tracking-[0.18em] font-bold underline decoration-2 underline-offset-2 hover:opacity-80 shrink-0"
            data-testid="defcon-alert-cta"
          >
            Watch the smackdown <ArrowRight size={13} />
          </Link>
        )}
        <button
          onClick={dismiss}
          className="ml-1 opacity-80 hover:opacity-100 shrink-0"
          aria-label="Dismiss"
          data-testid="defcon-alert-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
